// client/src/components/Navbar.tsx
"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useAuth } from '../contexts/AuthContext';
import { usePortfolio } from '../contexts/PortfolioContext';
import { useScenarios } from '../contexts/ScenarioContext';
import { useValueMasking } from '../contexts/ValueMaskingContext';
import PortfolioDropdown from './PortfolioDropdown';
import ScenarioDropdown from './ScenarioDropdown';
import TrialBanner from './TrialBanner';
import { AlertNotificationBadge } from './agent/AlertNotificationBadge';
import { AlertDetailView } from './agent/AlertDetailView';
import type { SpendingAlert } from './agent/types';
import { useAlerts } from '../hooks/useAlerts';
import {
    BarChart3,
    ReceiptPoundSterling,
    Banknote,
    Wallet,
    FileText,
    TrendingUp,
    Power,
    Menu,
    X,
    Eye,
    EyeOff,
    Gift,
    Wrench,
    TrendingUpDown,
    Settings,
    CreditCard,
} from 'lucide-react';
import { SUBSCRIPTION_TIERS } from '@/utils/subscriptionHelpers';
import MoodToggle from './dashboard/MoodToggle';

export default function Navbar() {
    const { user, signOut } = useAuth();
    const { activePortfolio } = usePortfolio();
    const { selectedScenario, setSelectedScenario } = useScenarios();
    const { isValueMasked, toggleValueMasking } = useValueMasking();
    const pathname = usePathname();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<SpendingAlert | null>(null);
    const [showAlertDetail, setShowAlertDetail] = useState(false);

    const { alerts, unreadCount, markAsRead, markAllAsRead } = useAlerts(10);

    const subscriptionTier = activePortfolio?.owner?.subscription_tier || activePortfolio?.users?.subscription_tier || 1;

    const getGiftIconClass = (tier: number) => {
        switch (tier) {
            case 1:
                return 'text-warning-500';
            case 2:
                return 'text-accessible-assets-500';
                return 'text-positive-500';
            case 3:
                return 'text-positive-500';
            case 5:
                return 'text-positive-500';
            case 99:
                return 'text-gray-400';
            default:
                return 'text-text-secondary';
        }
    };

    // Check if welcome screen is active - this is now a simplified check
    // The welcome screen logic is handled in DashboardClient, not here
    const isWelcomeScreenActive = false;

    const navigation = [
        { name: 'Dashboard', href: '/dashboard', icon: BarChart3, description: 'Your financial overview', color: '#14b8a6' },
        { name: 'Assets', href: '/assets', icon: Wallet, description: 'Track investments & holdings', color: '#0891b2' },
        { name: 'Income', href: '/income', icon: Banknote, description: 'Manage income sources', color: '#14b8a6' },
        { name: 'Expenses', href: '/expenses', icon: ReceiptPoundSterling, description: 'Monitor spending', color: '#f43f5e' },
        { name: 'Transactions', href: '/transactions', icon: FileText, description: 'View all transactions', color: '#14b8a6' },
        { name: 'Cashflow', href: '/cashflow', icon: TrendingUp, description: 'Cash in & out flow', color: '#6366f1' },
        { name: 'Simulations', href: '/simulations', icon: TrendingUpDown, description: 'Model future scenarios', color: '#0ea5e9' },
        { name: 'Tools', href: '/tools', icon: Wrench, description: 'Financial calculators', color: '#14b8a6' },
    ];

    const handleSignOut = async (surface: string) => {
        try {
            await signOut('user_click', { surface });
            // Redirect is now handled in the signOut function
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    return (
        <nav id="main-navbar" className="bg-white/80 sm:bg-white/50 shadow-lg border-b border-border-primary fixed top-0 left-0 right-0 z-[100] backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
                <div className="flex justify-between h-16">
                    <div className="flex">
                        <div className="flex-shrink-0 flex items-center">
                            <Link href="/dashboard" className="flex items-center gap-2">
                                <Image
                                    src="/logo.svg"
                                    alt="Delphina"
                                    width={28}
                                    height={28}
                                    className="h-7 w-auto"
                                    priority
                                />
                                <span className="text-2xl text-positive-600" style={{ letterSpacing: '-0.05em', fontWeight: 800 }}>
                                    Delphina.
                                </span>
                                <span title="We will remain in beta until we are ready to go general release." className="hidden sm:block px-2 py-1 text-xs font-semibold bg-warning-100 text-warning-800 rounded-full border border-warning-200">
                                    Beta
                                </span>
                                <span title="We will remain in beta until we are ready to go general release." className="block sm:hidden px-2 py-1 text-xs font-semibold bg-warning-100 text-warning-800 rounded-full border border-warning-200">
                                    β
                                </span>
                            </Link>
                        </div>
                        <div className="hidden sm:ml-6 sm:flex space-x-2">
                            {!isWelcomeScreenActive && navigation.map((item) => {
                                const isActive = pathname === item.href;
                                return (
                                    <Link
                                        key={item.name}
                                        href={item.href}
                                        role="link"
                                        data-testid={"navbar_" + item.name.toLowerCase()}
                                        className={`${isActive
                                            ? 'border-accent-positive text-text-positive'
                                            : 'border-transparent text-text-secondary hover:border-positive-300 hover:text-positive-500'
                                            } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200 relative navbar-icon mr-1`}
                                    >
                                        <item.icon className={`h-4 w-4 ${isActive ? 'mr-2' : 'mx-1'}`} />
                                        <span className={`${isActive ? '' : 'hidden'}`}>{item.name}</span>
                                        {!isActive && (
                                            <span
                                                className="navbar-tooltip"
                                                style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    marginTop: '2px',
                                                    padding: '6px 12px',
                                                    backgroundColor: '#111827',
                                                    borderLeft: `3px solid ${item.color}`,
                                                    color: 'white',
                                                    fontSize: '12px',
                                                    borderRadius: '8px',
                                                    whiteSpace: 'nowrap',
                                                    zIndex: 9999,
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                }}
                                            >
                                                <span style={{ position: 'absolute', top: '-6px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: `6px solid ${item.color}` }}></span>
                                                <span style={{ fontWeight: 600, display: 'block', textWrap: 'nowrap' }}>{item.name}</span>
                                            </span>
                                        )}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                    <div className="hidden sm:ml-6 sm:flex sm:items-center">
                        <div className="flex items-center space-x-4">
                            <ScenarioDropdown
                                selectedScenario={selectedScenario}
                                onScenarioChange={setSelectedScenario}
                                className="mr-4"
                            />
                            <PortfolioDropdown />
                            {user && (
                                <>
                                    <Link
                                        href="/subscription?tab=referral"
                                        className="cursor-pointer p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200 relative navbar-icon"
                                        aria-label="Referral Program"
                                    >
                                        <Gift className={`h-5 w-5 ${getGiftIconClass(subscriptionTier)}`} />
                                        <span
                                            className="navbar-tooltip"
                                            style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                marginTop: '8px',
                                                padding: '6px 12px',
                                                backgroundColor: '#111827',
                                                borderLeft: '3px solid #14b8a6',
                                                color: 'white',
                                                fontSize: '12px',
                                                borderRadius: '8px',
                                                whiteSpace: 'nowrap',
                                                zIndex: 9999,
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                display: 'none',
                                            }}
                                        >
                                            <span style={{ position: 'absolute', top: '-6px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '6px solid #14b8a6' }}></span>
                                            <span style={{ fontWeight: 600, display: 'block', textWrap: 'nowrap' }}>Free gifts</span>
                                        </span>
                                    </Link>

                                    <button
                                        onClick={toggleValueMasking}
                                        className="cursor-pointer p-2 rounded-lg text-text-secondary hover:text-positive-500 transition-colors duration-200 relative navbar-icon"
                                    >
                                        {isValueMasked ? (
                                            <EyeOff className="h-5 w-5" />
                                        ) : (
                                            <Eye className="h-5 w-5" />
                                        )}
                                        <span
                                            className="navbar-tooltip"
                                            style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                marginTop: '8px',
                                                padding: '6px 12px',
                                                backgroundColor: '#111827',
                                                borderLeft: '3px solid #14b8a6',
                                                color: 'white',
                                                fontSize: '12px',
                                                borderRadius: '8px',
                                                whiteSpace: 'nowrap',
                                                zIndex: 9999,
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                display: 'none',
                                            }}
                                        >
                                            <span style={{ position: 'absolute', top: '-6px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '6px solid #14b8a6' }}></span>
                                            <span style={{ fontWeight: 600, display: 'block', textWrap: 'nowrap' }}>{isValueMasked ? 'Show values' : 'Hide values'}</span>
                                        </span>
                                    </button>
                                    <button data-testid="desktop_sign_out"
                                        onClick={() => handleSignOut('desktop_navbar')}
                                        className="cursor-pointer p-2 rounded-lg text-text-secondary hover:text-danger-500 transition-colors duration-200 relative navbar-icon"
                                        aria-label="Sign out"
                                    >
                                        <Power className="h-5 w-5" />
                                        <span
                                            className="navbar-tooltip"
                                            style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                marginTop: '8px',
                                                padding: '6px 12px',
                                                backgroundColor: '#111827',
                                                borderLeft: '3px solid #f43f5e',
                                                color: 'white',
                                                fontSize: '12px',
                                                borderRadius: '8px',
                                                whiteSpace: 'nowrap',
                                                zIndex: 9999,
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                display: 'none',
                                            }}
                                        >
                                            <span style={{ position: 'absolute', top: '-6px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '6px solid #f43f5e' }}></span>
                                            <span style={{ fontWeight: 600, display: 'block', textWrap: 'nowrap' }}>Sign out</span>
                                        </span>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center sm:hidden">
                        <div className="flex items-center flex-1">
                            <button
                                data-testid="mobile-menu-button"
                                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                                aria-expanded={mobileMenuOpen}
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="cursor-pointer inline-flex items-center justify-center p-2 rounded-md text-text-secondary hover:text-text-positive hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent-positive"
                            >
                                {mobileMenuOpen ? (
                                    <X className="block h-6 w-6" aria-hidden="true" />
                                ) : (
                                    <Menu className="block h-6 w-6" aria-hidden="true" />
                                )}
                            </button>
                        </div>
                        <div className="flex items-center space-x-1">
                            {user && (
                                <>
                                    <Link
                                        href="/subscription?tab=referral"
                                        className="cursor-pointer p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                        title="Referral Program"
                                        aria-label="Referral Program"
                                    >
                                        <Gift className={`h-5 w-5 ${getGiftIconClass(subscriptionTier)}`} />
                                    </Link>
                                    <button
                                        onClick={toggleValueMasking}
                                        className="cursor-pointer p-2 rounded-lg text-text-secondary hover:text-positive-500  hover:bg-warning-500 transition-colors duration-200"
                                        title={isValueMasked ? "Unmask financial values" : "Mask financial values"}
                                    >
                                        {isValueMasked ? (
                                            <EyeOff className="h-5 w-5" />
                                        ) : (
                                            <Eye className="h-5 w-5" />
                                        )}
                                    </button>

                                    <button
                                        onClick={() => handleSignOut('mobile_power_icon')}
                                        className="cursor-pointer p-2 rounded-lg text-text-secondary hover:text-text-positive hover:bg-gray-200 transition-colors duration-200"
                                        title="Sign Out"
                                        data-testid="mobile_sign_out"
                                        aria-label="Sign Out"
                                    >
                                        <Power className="h-5 w-5" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            {mobileMenuOpen && (
                <div className="sm:hidden bg-white-80 border-t border-gray-300">
                    {user && <TrialBanner variant="menu" onNavigate={() => setMobileMenuOpen(false)} />}
                    <div className="pt-3 pb-2 px-4">
                        <MoodToggle />
                    </div>
                    <div className="pt-2 pb-3 grid grid-cols-2 gap-1">
                        {!isWelcomeScreenActive && navigation.map((item) => {
                            const isActive = pathname === item.href;
                            return (
                                <Link
                                    key={item.name}
                                    href={item.href}
                                    className={`${isActive
                                        ? 'bg-accent-positive-light border-accent-positive text-accent-positive'
                                        : 'border-transparent text-text-secondary hover:bg-gray-200 hover:border-gray-300 hover:text-positive-500'
                                        } block pl-3 pr-4 py-2 border-l-4 text-base font-medium flex items-center`}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <item.icon className="h-5 w-5 mr-3" />
                                    {item.name}
                                </Link>
                            );
                        })}
                    </div>
                    <div className="pt-4 pb-3 border-t border-gray-300">
                        <div className="mt-3 px-2 space-y-2">
                            <div className="mb-2 w-full">
                                <ScenarioDropdown
                                    selectedScenario={selectedScenario}
                                    onScenarioChange={setSelectedScenario}
                                    className="w-full"
                                    onMobileMenuClose={() => setMobileMenuOpen(false)}
                                />
                            </div>
                            <PortfolioDropdown mobile={true} onMobileMenuClose={() => setMobileMenuOpen(false)} />
                            <div className="flex gap-2 px-2">
                                <Link
                                    href="/settings"
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <Settings className="h-4 w-4" />
                                    Settings
                                </Link>
                                <Link
                                    href="/subscription"
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <CreditCard className="h-4 w-4" />
                                    Subscription
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Alert Detail Modal */}
            {showAlertDetail && selectedAlert && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setShowAlertDetail(false)} />
                    <div className="relative z-50 w-full max-w-lg mx-4">
                        <AlertDetailView
                            alert={selectedAlert}
                            onClose={() => setShowAlertDetail(false)}
                            onMarkAsRead={() => {
                                markAsRead(selectedAlert.id);
                            }}
                        />
                    </div>
                </div>
            )}
        </nav>
    );
}
